import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface OrgBranding {
  logoUrl: string | null;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  primaryColor: string | null;
  defaultTheme: 'light' | 'dark' | null;
  orgName: string | null;
  loading: boolean;
}

export function useOrgBranding(): OrgBranding {
  const { profile } = useAuth();
  const { resolvedTheme } = useTheme();
  const organizationId = profile?.organization_id;

  const { data, isLoading } = useQuery({
    queryKey: ['org-branding', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data: settings, error } = await supabase
        .from('organization_settings')
        .select(
          'primary_color, logo_url, logo_light_url, logo_dark_url, trade_logo_url, default_theme'
        )
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error || !settings) {
        // Fallback: try organizations table
        const { data: org } = await supabase
          .from('organizations')
          .select('name, logo_url, logo_light_url, logo_dark_url, primary_color')
          .eq('id', organizationId)
          .maybeSingle();

        return {
          orgName: org?.name ?? null,
          logoUrl: org?.logo_url ?? null,
          logoLightUrl: org?.logo_light_url ?? null,
          logoDarkUrl: org?.logo_dark_url ?? null,
          primaryColor: org?.primary_color ?? null,
          defaultTheme: null,
        };
      }

      // Also fetch org name
      const { data: org } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', organizationId)
        .maybeSingle();

      return {
        orgName: org?.name ?? null,
        logoUrl: settings.logo_url ?? null,
        logoLightUrl: settings.logo_light_url,
        logoDarkUrl: settings.logo_dark_url,
        tradLogoUrl: settings.trade_logo_url ?? null,
        primaryColor: settings.primary_color,
        defaultTheme: settings.default_theme as 'light' | 'dark' | null,
      };
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Fallback chain matching pwa-handel: themed logo → generic logo → trade logo
  const logoLightUrl = data?.logoLightUrl ?? null;
  const logoDarkUrl = data?.logoDarkUrl ?? null;
  const genericLogoUrl = data?.logoUrl ?? null;
  const tradeLogoUrl = data?.tradLogoUrl ?? null;

  const resolvedLogo = resolvedTheme === 'dark'
    ? (logoDarkUrl || logoLightUrl || genericLogoUrl || tradeLogoUrl)
    : (logoLightUrl || logoDarkUrl || genericLogoUrl || tradeLogoUrl);

  return {
    logoUrl: resolvedLogo,
    logoLightUrl,
    logoDarkUrl,
    primaryColor: data?.primaryColor ?? null,
    defaultTheme: data?.defaultTheme ?? null,
    orgName: data?.orgName ?? null,
    loading: isLoading,
  };
}
